import React, { useState, useEffect, useRef } from 'react';
import { api, adoptFreshTokens } from '../utils/api';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, QrCode, Key, Copy, Download, RefreshCw, AlertTriangle } from 'lucide-react';

// WP-2E added two optional props. Both default to the old behaviour, so every
// existing use of this component is untouched:
//   onEnabled — called once the person has seen and saved their recovery codes
//               and pressed Done. The 2FA set-up prompt uses it to close itself.
//   embedded  — the component is already inside a titled panel or modal, so it
//               drops its own outer card (background, border padding) and just
//               fills the space it is given.
//   autoStart — go straight to the QR code instead of showing the status view
//               with its "Enable Two-Factor Authentication" button first. The
//               set-up prompt at login uses it: the person has already said yes.
//   onStage   — called with 'status', 'setup' or 'recovery-codes' whenever the
//               screen changes, so a caller can hide its own Back button once
//               the recovery codes are showing.
//   onDisabled — called once 2FA has been switched off. "My account" uses it to
//               re-read the signed-in person, because switching 2FA off can put
//               a compulsory set-up back and the app must see that straight away.
const TwoFactorSettings = ({ onEnabled = null, embedded = false, autoStart = false, onStage = null, onDisabled = null }) => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ totp_enabled: false, recovery_codes_remaining: 0 });
  const [setupMode, setSetupMode] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  // WP-2E: true only when 2FA was just switched on in this visit. The recovery
  // codes screen is also reached by regenerating codes, and that must not tell
  // the set-up prompt that enrolment finished.
  const [justEnabled, setJustEnabled] = useState(false);
  // WP-2E round 2: autoStart must only ever fire on the first read of the
  // status, never again after enabling, disabling or regenerating codes.
  const autoStartedRef = useRef(false);

  // WP-2E: inside a modal or a titled panel this component supplies no card of
  // its own, so the chrome is not drawn twice.
  const cardClass = embedded ? 'space-y-6' : 'bg-gray-800 rounded-lg p-6 space-y-6';

  useEffect(() => {
    fetchStatus();
  }, []);

  // WP-2E round 2: tell the caller which screen is showing. The set-up prompt
  // uses it to take its Back button away once the recovery codes are up, because
  // going back from there would throw away codes that are shown only once.
  useEffect(() => {
    if (!onStage) return;
    onStage(showRecoveryCodes ? 'recovery-codes' : (setupMode ? 'setup' : 'status'));
  }, [onStage, showRecoveryCodes, setupMode]);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/auth/2fa/status');
      setStatus(response.data);
      // WP-2E round 2: the login prompt's "Set up now" lands here, so show the
      // QR code straight away instead of asking the person to press Enable too.
      if (autoStart && !autoStartedRef.current && !response.data?.totp_enabled) {
        autoStartedRef.current = true;
        await startSetup();
      }
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const startSetup = async () => {
    setLoading(true);
    try {
      const response = await api.post('/auth/2fa/setup');
      setSetupData(response.data);
      setSetupMode(true);
    } catch (error) {
      toast.error(error.friendlyMessage || 'Failed to start 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/2fa/verify-and-enable', { code: verificationCode });
      setRecoveryCodes(response.data.recovery_codes);
      setShowRecoveryCodes(true);
      setJustEnabled(true);
      setSetupMode(false);
      setSetupData(null);
      setVerificationCode('');
      toast.success('Two-factor authentication enabled!');
      fetchStatus();
    } catch (error) {
      toast.error(error.friendlyMessage || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async () => {
    if (!disablePassword) {
      toast.error('Please enter your password');
      return;
    }

    setLoading(true);
    try {
      const { data: disabled } = await api.post('/auth/2fa/disable', { password: disablePassword });
      // D-SS4: switching your own 2FA off ends every session you have open.
      // The server hands this browser a replacement pair so the click does not
      // sign you out of the screen you are standing on.
      adoptFreshTokens(disabled);
      toast.success('Two-factor authentication disabled');
      setShowDisableConfirm(false);
      setDisablePassword('');
      fetchStatus();
      // WP-2E: tell the caller, so the app can re-read the signed-in person.
      // The server puts back the reminder count and any compulsory set-up when
      // 2FA goes off; without this the app keeps the stale copy and the
      // compulsory set-up prompt would stay away for the rest of the session.
      if (onDisabled) onDisabled();
    } catch (error) {
      toast.error(error.friendlyMessage || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const regenerateRecoveryCodes = async () => {
    if (regenerateCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/2fa/regenerate-recovery-codes', { code: regenerateCode });
      setRecoveryCodes(response.data.recovery_codes);
      setShowRecoveryCodes(true);
      setShowRegenerateModal(false);
      setRegenerateCode('');
      toast.success('New recovery codes generated!');
      fetchStatus();
    } catch (error) {
      toast.error(error.friendlyMessage || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
      toast.success('Secret copied to clipboard');
    }
  };

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    toast.success('Recovery codes copied to clipboard');
  };

  const downloadRecoveryCodes = () => {
    const content = `Misty Manufacturing - Recovery Codes\n${'='.repeat(40)}\n\nStore these codes in a safe place.\nEach code can only be used once.\n\n${recoveryCodes.join('\n')}\n\nGenerated: ${new Date().toLocaleString()}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'misty-manufacturing-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Recovery codes downloaded');
  };

  if (loading && !setupMode && !showRecoveryCodes) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  // Recovery Codes Display
  if (showRecoveryCodes) {
    return (
      <div className={cardClass}>
        <div className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
            <Key className="h-8 w-8 text-green-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Recovery Codes</h3>
          <p className="text-gray-400 text-sm">
            Save these codes in a secure place. You can use them to access your account if you lose your authenticator device.
          </p>
        </div>

        <div className="bg-gray-900 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-2">
            {recoveryCodes.map((code, index) => (
              <div key={index} className="font-mono text-center py-2 bg-gray-800 rounded text-yellow-400">
                {code}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-200">
              <p className="font-medium mb-1">Important:</p>
              <ul className="list-disc list-inside space-y-1 text-yellow-200/80">
                <li>Each code can only be used once</li>
                <li>Store these codes separately from your device</li>
                <li>If you lose both your device and codes, you may lose access</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={copyRecoveryCodes}
            className="flex-1 misty-button bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center gap-2"
          >
            <Copy className="h-4 w-4" />
            Copy Codes
          </button>
          <button
            onClick={downloadRecoveryCodes}
            className="flex-1 misty-button bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center gap-2"
          >
            <Download className="h-4 w-4" />
            Download
          </button>
        </div>

        {/* WP-2E: when a caller is waiting for enrolment to finish (the set-up
            prompt at login), the last press says Done and hands control back.
            Everywhere else the button is exactly what it always was. */}
        {onEnabled && justEnabled ? (
          <button
            onClick={() => {
              setShowRecoveryCodes(false);
              setJustEnabled(false);
              onEnabled();
            }}
            className="w-full misty-button misty-button-primary"
            title="Done. I have saved my recovery codes."
            aria-label="Done. I have saved my recovery codes. Finish setting up two-factor authentication."
          >
            Done
          </button>
        ) : (
          <button
            onClick={() => setShowRecoveryCodes(false)}
            className="w-full misty-button misty-button-primary"
          >
            I&apos;ve Saved My Codes
          </button>
        )}
      </div>
    );
  }

  // Setup Mode
  if (setupMode && setupData) {
    return (
      <div className={cardClass}>
        <div className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-yellow-400/20 rounded-full flex items-center justify-center">
            <QrCode className="h-8 w-8 text-yellow-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Setup Two-Factor Authentication</h3>
          <p className="text-gray-400 text-sm">
            Scan the QR code with Microsoft Authenticator, Google Authenticator, or any TOTP app
          </p>
        </div>

        {/* QR Code */}
        <div className="flex justify-center">
          <div className="bg-white p-4 rounded-lg">
            <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48" />
          </div>
        </div>

        {/* Manual Entry */}
        <div className="bg-gray-900 rounded-lg p-4">
          <p className="text-sm text-gray-400 mb-2">Or enter this code manually:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-sm text-yellow-400 bg-gray-800 px-3 py-2 rounded break-all">
              {setupData.secret}
            </code>
            <button
              onClick={copySecret}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
              title="Copy secret"
            >
              <Copy className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Verification */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Enter the 6-digit code from your app
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            className="misty-input w-full text-center text-2xl tracking-widest font-mono"
            placeholder="000000"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
        </div>

        <div className="flex gap-3">
          {/* WP-2E round 3: when a caller is driving this (the set-up prompt at
              login), it supplies its own way out — its "Back" button. Cancel
              here would drop the person on the "Enable Two-Factor
              Authentication" status card, which autoStart exists to skip and
              which the manual never mentions. One way out, not two. */}
          {/* WP-ESC review round 2: this button abandons the set-up, it is not
              the way out of the window around it, so it says so. My account
              holds its own X shut while the QR code is on screen, and with no
              wording here Escape reached past that locked X and pressed this
              Cancel, throwing a half-finished enrolment away. */}
          {!onEnabled && (
            <button
              onClick={() => {
                setSetupMode(false);
                setSetupData(null);
                setVerificationCode('');
              }}
              className="flex-1 misty-button bg-gray-700 hover:bg-gray-600 text-white"
              title="Abandon this two-factor set-up and go back to the two-factor screen"
              aria-label="Abandon this two-factor set-up and go back to the two-factor screen"
            >
              Cancel
            </button>
          )}
          <button
            onClick={verifyAndEnable}
            disabled={loading || verificationCode.length !== 6}
            className="flex-1 misty-button misty-button-primary disabled:opacity-50"
          >
            {loading ? 'Verifying...' : 'Enable 2FA'}
          </button>
        </div>
      </div>
    );
  }

  // Main Status View
  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {status.totp_enabled ? (
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-green-400" />
            </div>
          ) : (
            <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center">
              <ShieldOff className="h-6 w-6 text-gray-400" />
            </div>
          )}
          <div>
            <h3 className="text-lg font-semibold text-white">Two-Factor Authentication</h3>
            <p className={status.totp_enabled ? 'text-green-400 text-sm' : 'text-gray-400 text-sm'}>
              {status.totp_enabled ? 'Enabled' : 'Not enabled'}
            </p>
          </div>
        </div>
      </div>

      {status.totp_enabled ? (
        <div className="space-y-4">
          <div className="bg-gray-900 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Recovery Codes Remaining</p>
                <p className={`text-2xl font-bold ${status.recovery_codes_remaining <= 3 ? 'text-yellow-400' : 'text-white'}`}>
                  {status.recovery_codes_remaining}
                </p>
              </div>
              <button
                onClick={() => setShowRegenerateModal(true)}
                className="misty-button bg-gray-700 hover:bg-gray-600 text-white flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Regenerate
              </button>
            </div>
            {status.recovery_codes_remaining <= 3 && (
              <p className="text-xs text-yellow-400 mt-2">
                ⚠️ You&apos;re running low on recovery codes. Consider regenerating them.
              </p>
            )}
          </div>

          <button
            onClick={() => setShowDisableConfirm(true)}
            className="w-full misty-button bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-600/30"
          >
            Disable Two-Factor Authentication
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-400">
            Add an extra layer of security to your account. When enabled, you&apos;ll need to enter a code from your authenticator app when logging in.
          </p>
          <button
            onClick={startSetup}
            disabled={loading}
            className="w-full misty-button misty-button-primary flex items-center justify-center gap-2"
          >
            <ShieldCheck className="h-5 w-5" />
            {loading ? 'Loading...' : 'Enable Two-Factor Authentication'}
          </button>
        </div>
      )}

      {/* Disable Confirmation Modal */}
      {showDisableConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 space-y-4">
            <h3 className="text-lg font-bold text-white">Disable Two-Factor Authentication</h3>
            <p className="text-sm text-gray-400">
              This will remove the extra security from your account. Enter your password to confirm.
            </p>
            <input
              type="password"
              className="misty-input w-full"
              placeholder="Enter your password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDisableConfirm(false);
                  setDisablePassword('');
                }}
                className="flex-1 misty-button bg-gray-700 hover:bg-gray-600 text-white"
              >
                Cancel
              </button>
              <button
                onClick={disable2FA}
                disabled={loading || !disablePassword}
                className="flex-1 misty-button bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                {loading ? 'Disabling...' : 'Disable 2FA'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Codes Modal */}
      {showRegenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 space-y-4">
            <h3 className="text-lg font-bold text-white">Regenerate Recovery Codes</h3>
            <p className="text-sm text-gray-400">
              This will invalidate all existing recovery codes. Enter your current 2FA code to confirm.
            </p>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              className="misty-input w-full text-center text-xl tracking-widest font-mono"
              placeholder="000000"
              value={regenerateCode}
              onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRegenerateModal(false);
                  setRegenerateCode('');
                }}
                className="flex-1 misty-button bg-gray-700 hover:bg-gray-600 text-white"
              >
                Cancel
              </button>
              <button
                onClick={regenerateRecoveryCodes}
                disabled={loading || regenerateCode.length !== 6}
                className="flex-1 misty-button misty-button-primary disabled:opacity-50"
              >
                {loading ? 'Generating...' : 'Generate New Codes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSettings;
